// Flipmax public deal sizer - live panel edition (POST /api/v1/sizer)
// Updates the estimate panel as the user fills in valid fields. The one
// action is a three-field capture under the estimate (name, mobile, email)
// that posts the scenario to POST /api/apply/quick; the server prices it
// for a quote id and a loan officer texts two call times. The scenario is
// also saved to localStorage so the full /apply/ form can prefill.
//
// Every number in the panel is a value the pricer returned, picked and
// labeled by sizer-math.js. Nothing here composes a rate, a cap, or a fee.
//
// The scenario also lives in the page URL (quote-link.js), so the address
// bar is always a link someone else can open to the same deal and terms.
import * as sizer from './sizer-math.js';
import * as quoteLink from './quote-link.js';

(function() {
  const form = document.getElementById('quote-sizer-form');
  const panel = document.getElementById('quote-sizer-panel-content');
  const panelWrap = document.getElementById('quote-sizer-panel');
  const panelTitle = document.getElementById('quote-sizer-panel-title');
  const ariaStatus = document.getElementById('quote-sizer-aria-status');
  const submitBtn = document.getElementById('quote-sizer-submit');
  const resetBtn = document.getElementById('quote-sizer-reset');
  const summary = document.getElementById('quote-sizer-summary');
  const warning = document.getElementById('quote-sizer-warning');
  if (!form || !panel) return;

  const STORAGE_KEY = 'flipmax_quote_sizer_v1';
  const HANDOFF_KEY = 'flipmax_sizer_handoff';
  const CONTACT_KEY = 'flipmax_borrower_contact';
  const QUICK_URL = '/api/apply/quick';
  const API_URL = 'https://max.flipmax.com/api/v1/sizer';
  const DEBOUNCE_MS = 600;
  const COOLDOWN_MS = 1200;
  // On purchases the pricer is asked for its maximum leverage and for the
  // same deal at 20%, 10%, and 5% down (purchase advance targets in
  // sizer.CARD_LTPS). sizer.offerSet picks up to three cards from the
  // answers; requests that land on the same cap render once.
  const PHONE_HTML = '<a href="tel:8333547629" class="font-semibold text-fx-primary hover:underline">(833) FLIPMAX</a>';

  let debounceTimer = null;
  let isLoading = false;
  let pendingRefetch = false;
  let requestSeq = 0;
  let lastStatus = null;
  let currentOffer = null;
  let currentInputs = null;
  // The card a shared link asked for (its requested ltp), honored once.
  let linkedLtp = null;
  // The structure the borrower tapped ('max' for the pricer's maximum, else
  // its requested ltp). It stays selected as the numbers change.
  let chosenLtp = null;
  // The payload behind the offers on screen. A field losing focus fires
  // change without changing anything; the same payload is not re-priced.
  let pricedKey = null;

  // `pricer: false` marks a field the pricer never sees; it still travels in
  // the link, the handoff to /apply/, and the application.
  const STATES = ['DC','DE','FL','GA','MD','NC','NJ','PA','SC','TX','VA'];
  // In form order, so the progress strip names the next field on the page.
  const fields = {
    purchase_or_refi: { label: 'Loan purpose', required: true, allowed: ['purchase','refinance','cash_out'] },
    deal_type:        { label: 'Loan type', required: true, allowed: ['rtl','guc','dscr'] },
    address:          { label: 'Street address', required: false, maxLength: 200, pricer: false },
    city:             { label: 'City', required: true, maxLength: 100 },
    state:            { label: 'State', required: true, allowed: STATES,
                        invalid: 'Flipmax lends across the DMV, Greater Philadelphia, Charlotte, Atlanta, Orlando, and Dallas-Fort Worth.' },
    zip:              { label: 'ZIP code', required: false, pattern: /^\d{5}$/, invalid: 'ZIP code must be 5 digits.', pricer: false },
    purchase_price:   { label: 'Purchase price', required: true, type: 'number', min: 1, max: 99999999 },
    renovation_budget:{ label: 'Rehab budget', required: true, type: 'number', min: 0, max: 99999999 },
    arv:              { label: 'After-repair value', required: true, type: 'number', min: 1, max: 99999999 },
    experience_projects:{ label: 'Flips completed', required: true, type: 'integer', min: 0, max: 1000 },
    fico:             { label: 'Credit score', required: true, type: 'integer', min: 300, max: 850 },
    property_type:    { label: 'Property type', required: true, allowed: ['single_family','two_to_four','condo','townhouse'] },
    term_months:      { label: 'Term (months)', required: false, type: 'integer', min: 1, max: 24 }
  };

  const currencyFields = ['purchase_price','renovation_budget','arv'];

  // One tab strip sets deal_type. Flips and builds are purchases; a rental
  // keeps whichever purpose its own tab strip holds.
  const PRODUCTS = {
    flip: { deal_type: 'rtl', purchase_or_refi: 'purchase' },
    guc:  { deal_type: 'guc', purchase_or_refi: 'purchase' },
    dscr: { deal_type: 'dscr' }
  };

  function isRadioNodeList(el) {
    return el && el[0] && el[0].type === 'radio';
  }

  function isCurrencyField(name) {
    return currencyFields.indexOf(name) !== -1;
  }

  function getFieldValue(name) {
    const el = form.elements[name];
    if (!el) return '';
    if (isRadioNodeList(el)) {
      const checked = form.querySelector('input[name="' + name + '"]:checked');
      return checked ? checked.value : '';
    }
    if (el.type === 'select-one') return el.value;
    if (el.type === 'number') return el.value === '' ? '' : Number(el.value);
    return el.value.trim();
  }

  function setFieldValue(name, value) {
    const el = form.elements[name];
    if (!el) return;
    if (isRadioNodeList(el)) {
      const radio = form.querySelector('input[name="' + name + '"][value="' + value + '"]');
      if (radio) radio.checked = true;
      return;
    }
    if (el.type === 'select-one') {
      el.value = value;
      return;
    }
    el.value = (value === null || value === undefined) ? '' : value;
  }

  function formatCurrencyInput(input) {
    const raw = input.value.replace(/[^\d]/g, '');
    if (raw === '') {
      input.value = '';
      return;
    }
    const num = parseInt(raw, 10);
    input.value = isNaN(num) ? '' : num.toLocaleString('en-US');
  }

  function parseNumeric(name, raw) {
    if (raw === '' || raw === null || raw === undefined) return NaN;
    if (isCurrencyField(name)) {
      const cleaned = String(raw).replace(/,/g, '').replace(/[^0-9.]/g, '');
      if (cleaned === '') return NaN;
      return Number(cleaned);
    }
    return Number(raw);
  }

  function isBlank(v) {
    return v === '' || v === null || v === undefined;
  }

  // New construction and rentals are priced by a loan officer, not live.
  function isTeamSized() {
    const type = getFieldValue('deal_type');
    return type === 'guc' || type === 'dscr';
  }

  // ── Errors ──
  function showFieldError(name, msg) {
    const el = document.getElementById(name + '-error');
    if (el) { el.textContent = msg; el.classList.remove('hidden'); }
    const input = form.elements[name];
    if (input && !isRadioNodeList(input)) input.setAttribute('aria-invalid', 'true');
  }

  function hideFieldError(name) {
    const el = document.getElementById(name + '-error');
    if (el) { el.classList.add('hidden'); el.textContent = ''; }
    const input = form.elements[name];
    if (input && !isRadioNodeList(input)) input.removeAttribute('aria-invalid');
  }

  function clearAllErrors() {
    if (summary) { summary.classList.add('hidden'); summary.textContent = ''; }
    if (warning) { warning.classList.add('hidden'); warning.textContent = ''; }
    Object.keys(fields).forEach(hideFieldError);
  }

  function showSummary(msg) {
    if (!summary) return;
    summary.textContent = msg;
    summary.classList.remove('hidden');
  }

  function showWarning(msg) {
    if (!warning) return;
    warning.textContent = msg;
    warning.classList.remove('hidden');
  }

  function validateField(name) {
    const spec = fields[name];
    if (!spec) return null;
    const raw = getFieldValue(name);
    const num = parseNumeric(name, raw);

    if (spec.required && isBlank(raw)) {
      return spec.label + ' is required';
    }
    if (isBlank(raw)) return null;

    if (spec.type === 'number' || spec.type === 'integer') {
      if (Number.isNaN(num)) return spec.label + ' must be a number';
      if (spec.type === 'integer' && !Number.isInteger(num)) return spec.label + ' must be a whole number';
      if (spec.min !== undefined && num < spec.min) {
        return spec.min === 1 && isCurrencyField(name)
          ? spec.label + ' must be greater than $0'
          : spec.label + ' must be at least ' + spec.min;
      }
      if (spec.max !== undefined && num > spec.max) return spec.label + ' must be no more than ' + spec.max;
    }
    if (spec.maxLength !== undefined && String(raw).length > spec.maxLength) {
      return spec.label + ' must be ' + spec.maxLength + ' characters or fewer';
    }
    if (spec.allowed && spec.allowed.indexOf(String(raw)) === -1) {
      return spec.invalid || 'Please select a valid ' + spec.label.toLowerCase();
    }
    if (spec.pattern && !spec.pattern.test(String(raw))) return spec.invalid;
    if (name === 'city') return cityMismatch();
    return null;
  }

  function validate() {
    const errors = {};
    Object.keys(fields).forEach(function(name) {
      const msg = validateField(name);
      if (msg) errors[name] = msg;
    });
    return errors;
  }

  // ── Formatting ──
  const formatCurrency = sizer.formatCurrency;
  const formatPercent = sizer.formatPercent;
  const formatPoints = sizer.formatPoints;

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function(m) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m];
    });
  }

  function setAriaStatus(msg) {
    if (ariaStatus) ariaStatus.textContent = msg || '';
  }

  // The panel title states the finding. Outside the approved state it says
  // what the panel is waiting on, in the same sentence form.
  const EMPTY_TITLE = 'Fill in the deal to see what you bring to close.';
  function setPanelTitle(text) {
    if (panelTitle) panelTitle.textContent = text || EMPTY_TITLE;
  }

  // ── Mobile summary bar ──
  // Below the desktop breakpoint this bar carries the estimate while the
  // borrower types: a progress strip until the deal is priceable, then the
  // loan amount (counted up to each new figure), the terms, the cash to
  // close, and one chip per structure. Every number is one the panel shows.
  const summaryBar = (function() {
    const $ = function(id) { return document.getElementById(id); };
    const bar = $('quote-sizer-bar');
    const noop = function() {};
    if (!bar) return { progress: noop, pending: noop, settle: noop, loading: noop, offer: noop, message: noop, setAction: noop };
    const el = {
      progress: $('quote-sizer-bar-progress'), chips: $('quote-sizer-bar-chips'),
      message: $('quote-sizer-bar-message'), text: $('quote-sizer-bar-text'), sub: $('quote-sizer-bar-sub'),
      offer: $('quote-sizer-bar-offer'), loan: $('quote-sizer-bar-loan'), updating: $('quote-sizer-bar-updating'),
      terms: $('quote-sizer-bar-terms'), cash: $('quote-sizer-bar-cash'), action: $('quote-sizer-bar-action')
    };
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let panelInView = false;
    let started = false;
    let shownLoan = 0;
    let tweenFrame = null;

    function refresh() {
      bar.hidden = panelInView || !started;
      // Keep the page's last lines clear of the bar.
      document.body.style.paddingBottom = bar.hidden ? '' : bar.offsetHeight + 'px';
    }

    if ('IntersectionObserver' in window && panelWrap) {
      new IntersectionObserver(function(entries) {
        panelInView = entries.some(function(e) { return e.isIntersecting; });
        refresh();
      }, { threshold: 0.15 }).observe(panelWrap);
    }
    // The bar slides in once the borrower starts on the deal.
    function start() { if (!started) { started = true; refresh(); } }
    form.addEventListener('input', start);
    form.addEventListener('change', start);

    function setProgress(fraction) {
      if (el.progress) el.progress.style.width = Math.round(Math.max(0, Math.min(1, fraction)) * 100) + '%';
    }

    function showMessage(text, sub) {
      el.offer.hidden = true;
      el.chips.hidden = true;
      el.message.hidden = false;
      el.text.textContent = text;
      el.sub.textContent = sub || '';
      el.sub.hidden = !sub;
      refresh();
    }

    // Count the loan amount from what is on screen to the new figure.
    function tweenLoan(to) {
      if (tweenFrame) cancelAnimationFrame(tweenFrame);
      const from = shownLoan;
      shownLoan = to;
      if (reduceMotion || from === to) { el.loan.textContent = formatCurrency(to); return; }
      const t0 = performance.now();
      const duration = 650;
      function step(now) {
        const p = Math.min(1, (now - t0) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        el.loan.textContent = formatCurrency(from + (to - from) * eased);
        tweenFrame = p < 1 ? requestAnimationFrame(step) : null;
      }
      tweenFrame = requestAnimationFrame(step);
      el.loan.classList.remove('sizer-bar-flash');
      void el.loan.offsetWidth;
      el.loan.classList.add('sizer-bar-flash');
    }

    function renderChips(offers, selectedIndex, onPick) {
      if (!offers || offers.length < 2) { el.chips.hidden = true; el.chips.innerHTML = ''; return; }
      el.chips.innerHTML = offers.map(function(offer, i) {
        const down = sizer.downPaymentPercent(offer, currentInputs);
        const label = down === null ? 'Option ' + (i + 1) : formatPercent(down) + ' down';
        const on = i === selectedIndex;
        return '<button type="button" data-bar-offer="' + i + '" aria-pressed="' + on + '" class="flex-1 text-xs font-semibold py-1.5 rounded-full border transition-colors ' +
          (on ? 'bg-fx-primary text-fx-on-primary border-fx-primary' : 'bg-white text-fx-secondary border-fx-secondary/25') + '">' + escapeHtml(label) + '</button>';
      }).join('');
      el.chips.hidden = false;
      el.chips.querySelectorAll('[data-bar-offer]').forEach(function(btn) {
        btn.addEventListener('click', function() { onPick(parseInt(btn.getAttribute('data-bar-offer'), 10)); });
      });
    }

    return {
      // done of total required fields; nextLabel names the next one.
      progress: function(done, total, nextLabel) {
        setProgress(total ? done / total : 0);
        const left = total - done;
        showMessage(
          left > 0 ? left + ' field' + (left === 1 ? '' : 's') + ' to your loan' : 'Deal entered',
          left > 0 && nextLabel ? 'Next: ' + nextLabel : 'Your loan amount and rate appear here.'
        );
      },
      // A field changed: move the strip now and mark any shown numbers stale
      // until the debounced price lands.
      pending: function(done, total) {
        setProgress(total ? done / total : 0);
        if (!el.offer.hidden) { el.updating.hidden = false; el.offer.classList.add('sizer-bar-stale'); }
      },
      // Nothing changed after all: the numbers on screen stand.
      settle: function() {
        el.updating.hidden = true;
        el.offer.classList.remove('sizer-bar-stale');
      },
      // A new price is on the way: keep the last numbers, marked stale.
      loading: function() {
        setProgress(1);
        if (el.offer.hidden) { showMessage('Pricing your deal...', 'Loan, rate, and cash to close in a moment.'); return; }
        el.updating.hidden = false;
        el.offer.classList.add('sizer-bar-stale');
      },
      offer: function(offer, offers, selectedIndex, onPick) {
        setProgress(1);
        el.message.hidden = true;
        el.offer.hidden = false;
        el.updating.hidden = true;
        el.offer.classList.remove('sizer-bar-stale');
        tweenLoan(offer.loan_amount || 0);
        const ratePoints = sizer.ratePointsText(offer);
        const term = offer.term_months ? offer.term_months + ' mo' : null;
        el.terms.textContent = [ratePoints, term].filter(Boolean).join(' \u00b7 ');
        const card = sizer.cardText(offer, currentInputs);
        const cash = [];
        if (offer.cash_to_close !== null) cash.push(card.total + ' to close');
        if (card.payment) cash.push(card.payment + '/mo');
        el.cash.textContent = cash.join(' \u00b7 ');
        renderChips(offers, selectedIndex, onPick);
        refresh();
      },
      message: function(text, sub) {
        shownLoan = 0;
        showMessage(text, sub);
      },
      // 'estimate' points at the panel; 'capture' at the one action.
      setAction: function(mode) {
        if (mode === 'capture') { el.action.textContent = 'Get call times'; el.action.href = '#quote-capture'; }
        else { el.action.textContent = 'See estimate'; el.action.href = '#quote-sizer-panel'; }
      }
    };
  })();

  // ── City belongs to the selected state ──
  // The list comes from the Census places file (src/_data/cities_by_state.json,
  // served at /cities-by-state.json). A city that is not in the selected
  // state blocks the estimate and the capture, because the pricer reads the
  // city. If the list cannot be loaded nothing is blocked.
  const CITIES_URL = '/cities-by-state.json';
  const cityOptions = document.getElementById('city-options');
  let citiesByState = null;

  fetch(CITIES_URL).then(function(resp) {
    return resp.ok ? resp.json() : null;
  }).then(function(json) {
    citiesByState = json && typeof json === 'object' ? json : null;
    refreshCityOptions();
    if (getFieldValue('city') && getFieldValue('state')) {
      const msg = cityMismatch();
      if (msg) { showFieldError('city', msg); renderPanelIncomplete(validate()); }
    }
  }).catch(function() {});

  function normalizeCity(value) {
    return String(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }

  function stateName(code) {
    const option = form.querySelector('select[name="state"] option[value="' + code + '"]');
    return option ? option.textContent.trim() : code;
  }

  function cityMismatch() {
    if (!citiesByState) return null;
    const state = getFieldValue('state');
    const city = getFieldValue('city');
    if (!state || !city) return null;
    const list = citiesByState[state];
    if (!Array.isArray(list) || !list.length) return null;
    const wanted = normalizeCity(city);
    if (list.some(function(c) { return normalizeCity(c) === wanted; })) return null;
    return city + ' is not in ' + stateName(state) + '. Check the city or the state.';
  }

  function refreshCityOptions() {
    if (!cityOptions || !citiesByState) return;
    const list = citiesByState[getFieldValue('state')] || [];
    cityOptions.innerHTML = list.map(function(c) { return '<option value="' + escapeHtml(c) + '"></option>'; }).join('');
  }

  function track(eventName, params) {
    try {
      if (typeof window.gtag === 'function') window.gtag('event', eventName, params || {});
    } catch (e) {}
  }

  // ── Payload ──
  // What the pricer is asked. Fields marked pricer: false stay out.
  function getRequestPayload() {
    const payload = {};
    Object.keys(fields).forEach(function(name) {
      if (fields[name].pricer === false) return;
      let raw = getFieldValue(name);
      if (isBlank(raw)) {
        if (name === 'term_months') raw = 12;
        else return;
      }
      if (isCurrencyField(name)) {
        payload[name] = parseFloat(String(raw).replace(/,/g, ''));
      } else if (['fico','experience_projects','term_months'].indexOf(name) !== -1) {
        payload[name] = parseInt(raw, 10);
      } else {
        payload[name] = raw;
      }
    });
    return payload;
  }

  // The scenario as the application wants it: the pricer's inputs plus the
  // fields it never sees (street address and ZIP).
  function getScenario() {
    const scenario = getRequestPayload();
    Object.keys(fields).forEach(function(name) {
      if (fields[name].pricer !== false) return;
      const value = getFieldValue(name);
      if (!isBlank(value)) scenario[name] = value;
    });
    return scenario;
  }

  function requiredFields() {
    return Object.keys(fields).filter(function(n) { return fields[n].required; });
  }

  function remainingFields() {
    return requiredFields().filter(function(name) { return isBlank(getFieldValue(name)); });
  }

  // ── The one action ──
  function hideCta() {
    capture.hide();
  }

  function showCta(lead) {
    capture.show(lead);
  }

  function saveHandoff(offer) {
    currentOffer = offer || null;
    try {
      localStorage.setItem(HANDOFF_KEY, JSON.stringify({
        savedAt: Date.now(),
        inputs: getScenario(),
        offer: offer || null
      }));
    } catch (e) {}
    syncUrl();
  }

  // ── The link ──
  // The address bar mirrors the form and the picked card, so copying it at
  // any point shares the deal. Nothing is pushed onto history.
  function currentUrl() {
    const values = {};
    Object.keys(fields).forEach(function(name) { values[name] = getFieldValue(name); });
    const ltp = currentOffer && currentOffer.requested_ltp != null ? currentOffer.requested_ltp : null;
    return quoteLink.shareUrl(location.origin + location.pathname, values, ltp);
  }

  function syncUrl() {
    try {
      const url = currentUrl();
      if (url !== location.href) history.replaceState(history.state, '', url);
    } catch (e) {}
  }

  function loadFromUrl() {
    const values = quoteLink.fromQuery(location.search);
    Object.keys(fields).forEach(function(name) {
      if (values[name] !== undefined) setFieldValue(name, values[name]);
    });
    linkedLtp = values.ltp != null ? values.ltp : null;
  }

  // ── Panel rendering ──
  function renderPanelIncomplete(validationErrors) {
    pricedKey = null;
    const missing = remainingFields();
    setPanelTitle(EMPTY_TITLE);
    let body = '<p class="text-sm text-gray-500">Loan amount, rate, and points appear here as you type.</p>';
    if (missing.length > 0) {
      const labels = missing.slice(0, 2).map(function(n) { return fields[n].label; }).join(', ');
      body += '<p class="text-sm text-fx-primary font-semibold mt-2">' + missing.length + ' field' + (missing.length === 1 ? '' : 's') + ' left</p>' +
        '<p class="text-xs text-gray-500 mt-1">Next: ' + escapeHtml(labels) + (missing.length > 2 ? ' ...' : '') + '</p>';
    } else {
      const errs = validationErrors || validate();
      const firstKey = Object.keys(errs)[0];
      if (firstKey) {
        body += '<p class="text-xs text-red-600 mt-2">' + escapeHtml(errs[firstKey]) + '</p>';
      } else {
        body += '<p class="text-xs text-gray-500 mt-2">Almost there.</p>';
      }
    }
    panel.innerHTML = '<div class="text-center py-6">' + body + '</div>';
    hideCta();
    setAriaStatus('');
    const required = requiredFields();
    const left = remainingFields();
    summaryBar.progress(required.length - left.length, required.length, left.length ? fields[left[0]].label : null);
  }

  function renderPanelLoading() {
    summaryBar.loading();
    panel.innerHTML = '<div class="text-center py-6">' +
      '<div class="inline-flex items-center gap-2 text-sm text-gray-500">' +
        '<svg class="animate-spin h-4 w-4 text-fx-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">' +
          '<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>' +
          '<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>' +
        '</svg>' +
        'Updating estimate...' +
      '</div>' +
      '</div>';
    hideCta();
  }

  function renderPanelTeamSized() {
    pricedKey = null;
    const rental = getFieldValue('deal_type') === 'dscr';
    const title = rental ? 'Rental loans are priced by our team.' : 'New construction is sized by our team.';
    const why = rental
      ? 'A rental loan is sized on the rent the property earns, so a loan officer prices it with you.'
      : 'Ground-up deals depend on the lot, plans, and draw schedule, so a loan officer sizes them with you.';
    panel.innerHTML = '<div class="sizer-result-animate text-center py-2">' +
      '<div class="inline-flex items-center justify-center w-12 h-12 rounded-full bg-fx-green-10 text-fx-primary mb-3">' +
        '<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path></svg>' +
      '</div>' +
      '<p class="text-sm text-gray-600">' + why + ' Send the deal, or call ' + PHONE_HTML + '.</p>' +
      '</div>';
    setPanelTitle(title);
    summaryBar.message(title, 'Send the deal and a loan officer texts you call times.');
    saveHandoff(null);
    showCta('Send the deal and a loan officer texts you two call times to price it with you.');
    setAriaStatus(title + ' Submit your deal to get terms.');
    const status = rental ? 'dscr' : 'guc';
    if (lastStatus !== status) track('sizer_result', { status: rental ? 'rental' : 'new_construction' });
    lastStatus = status;
  }

  function renderPanelApproved(data, altByLtp, payload) {
    currentInputs = payload || getRequestPayload();
    const primary = sizer.normalizeOffer(data, null);
    const byLtp = {};
    Object.keys(altByLtp || {}).forEach(function(ltp) {
      byLtp[ltp] = sizer.normalizeOffer(altByLtp[ltp], { ltp: Number(ltp) });
    });
    const offers = sizer.offerSet(primary, byLtp, currentInputs);
    if (!offers.length) {
      renderPanelError('No approved offers are available for this scenario.');
      return;
    }
    // A shared link names the card the sender picked; after that the
    // pricer's maximum leverage is the default, or the least cash down
    // when the maximum is not one of the cards.
    let selectedIndex = offers.indexOf(primary);
    if (selectedIndex === -1) selectedIndex = offers.length - 1;
    if (linkedLtp != null) {
      const linkedIndex = offers.findIndex(function(o) { return o.requested_ltp === linkedLtp; });
      if (linkedIndex !== -1) selectedIndex = linkedIndex;
      linkedLtp = null;
    } else if (chosenLtp != null) {
      const chosenIndex = chosenLtp === 'max'
        ? offers.indexOf(primary)
        : offers.findIndex(function(o) { return o.requested_ltp === chosenLtp; });
      if (chosenIndex !== -1) selectedIndex = chosenIndex;
    }
    const selectedOffer = offers[selectedIndex];
    setPanelTitle(sizer.panelTitle(selectedOffer));

    let html = '<div class="sizer-result-animate">';
    if (offers.length > 1) {
      html += '<p class="text-sm text-fx-secondary/70 mb-3">' + (offers.length === 3 ? 'Three' : 'Two') + ' ways to structure it. Tap one.</p>';
      html += '<div class="space-y-3 mb-5" id="offer-cards">';
      offers.forEach(function(offer, index) {
        html += renderOfferCard(offer, index, index === selectedIndex);
      });
      html += '</div>';
      html += '<p class="text-xs text-gray-500 -mt-3 mb-5">Monthly payment is interest only on the initial advance. It grows as rehab draws fund.</p>';
    }
    html += '<div id="offer-details">' + renderOfferDetails(selectedOffer) + '</div>';
    html += '</div>';

    panel.innerHTML = html;
    if (offers.length > 1) attachOfferCardListeners(offers);
    summaryBar.offer(selectedOffer, offers, selectedIndex, function(i) { selectOffer(i, offers); });
    updateCtaForOffer(selectedOffer);
    setAriaStatus('Approved. ' + (offers.length > 1 ? offers.length + ' offer options. ' : '') + renderOfferAria(selectedOffer) + '.');
    track('sizer_result', {
      status: 'approved',
      loan_amount: selectedOffer.loan_amount,
      ltp: selectedOffer.ltp,
      interest_rate: selectedOffer.interest_rate,
      purchase_or_refi: data.purchase_or_refi,
      property_state: getFieldValue('state')
    });
    lastStatus = 'approved';
  }

  function renderOfferCard(offer, index, selected) {
    const ratePoints = sizer.ratePointsText(offer);
    const text = sizer.cardText(offer, currentInputs || getRequestPayload());
    const selectedClass = selected ? 'border-fx-primary bg-fx-green-10' : 'border-fx-secondary/50 hover:border-fx-primary';
    const row = function(label, value) {
      return '<div class="flex justify-between gap-3 text-sm"><span class="text-fx-secondary/70">' + escapeHtml(label) + '</span>' +
        '<span class="font-semibold text-fx-secondary tabular-nums">' + escapeHtml(value) + '</span></div>';
    };

    let html = '<div class="offer-card p-4 rounded-lg border-2 cursor-pointer transition-all ' + selectedClass + '" data-offer-index="' + index + '" role="button" tabindex="0" aria-pressed="' + selected + '">' +
      '<div class="flex justify-between items-baseline gap-3 mb-3">' +
        (text.heading ? '<p class="text-base font-bold text-fx-secondary">' + escapeHtml(text.heading) + '</p>' : '<span></span>') +
        (ratePoints ? '<p class="text-sm font-bold text-fx-primary">' + escapeHtml(ratePoints) + '</p>' : '') +
      '</div>';
    if (text.rows.length) {
      html += '<div class="space-y-1">' + text.rows.map(function(r) { return row(r.label, r.value); }).join('') + '</div>';
    }
    if (text.total) {
      html += '<div class="mt-3 pt-3 border-t border-fx-secondary/15">' +
        '<p class="text-sm text-fx-secondary/70">Estimated cash to close</p>' +
        '<p class="text-2xl font-bold text-fx-secondary font-serif leading-tight">' + escapeHtml(text.total) + '</p>' +
        '</div>';
    }
    if (text.payment) html += '<div class="mt-2">' + row('Monthly payment', text.payment) + '</div>';
    return html + '</div>';
  }

  // Hierarchy: loan amount dominates, then rate, points, and term on one
  // line. One variable changes per level (size), colors and weights stay put.
  function renderOfferDetails(offer) {
    const loanAmount = offer.loan_amount != null ? formatCurrency(offer.loan_amount) : null;
    const ratePoints = sizer.ratePointsText(offer);
    const term = offer.term_months ? offer.term_months + ' month' + (Number(offer.term_months) === 1 ? '' : 's') : null;

    let html = '';
    if (loanAmount) {
      html += '<div class="mb-4">' +
        '<p class="text-sm text-gray-500">Loan amount</p>' +
        '<p class="text-2xl font-bold text-fx-secondary font-serif leading-tight">' + escapeHtml(loanAmount) + '</p>' +
        '</div>';
    }

    const rateLine = [ratePoints, term].filter(Boolean).join(', ');
    if (rateLine) {
      html += '<div>' +
        '<p class="text-sm text-gray-500">Rate, points, and term</p>' +
        '<p class="text-lg font-bold text-fx-secondary font-serif leading-tight">' + escapeHtml(rateLine) + '</p>' +
        '</div>';
    }
    return html;
  }

  function attachOfferCardListeners(offers) {
    const cards = panel.querySelectorAll('.offer-card');
    cards.forEach(function(card) {
      card.addEventListener('click', function() {
        selectOffer(parseInt(card.getAttribute('data-offer-index'), 10), offers);
      });
      card.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          selectOffer(parseInt(card.getAttribute('data-offer-index'), 10), offers);
        }
      });
    });
  }

  function selectOffer(index, offers) {
    const offer = offers[index];
    if (!offer) return;
    chosenLtp = offer.requested_ltp === null ? 'max' : offer.requested_ltp;
    const cards = panel.querySelectorAll('.offer-card');
    cards.forEach(function(c) {
      const isSelected = parseInt(c.getAttribute('data-offer-index'), 10) === index;
      c.setAttribute('aria-pressed', isSelected);
      if (isSelected) {
        c.classList.remove('border-fx-secondary/50', 'hover:border-fx-primary');
        c.classList.add('border-fx-primary', 'bg-fx-green-10');
      } else {
        c.classList.remove('border-fx-primary', 'bg-fx-green-10');
        c.classList.add('border-fx-secondary/50', 'hover:border-fx-primary');
      }
    });
    const details = document.getElementById('offer-details');
    if (details) details.innerHTML = renderOfferDetails(offer);
    setPanelTitle(sizer.panelTitle(offer));
    summaryBar.offer(offer, offers, index, function(i) { selectOffer(i, offers); });
    updateCtaForOffer(offer);
    setAriaStatus('Selected offer: ' + renderOfferAria(offer) + '.');
  }

  function updateCtaForOffer(offer) {
    saveHandoff(offer);
    showCta('Get Pre-Approval / Proof of Funds Now');
  }

  function renderOfferAria(offer) {
    return sizer.offerSummary(offer) || 'offer pending';
  }

  function renderPanelDeclined(errors) {
    pricedKey = null;
    const messages = (Array.isArray(errors) ? errors : []).filter(function(e) { return typeof e === 'string' && e.trim(); });
    if (!messages.length) messages.push('This scenario does not meet current guidelines.');
    const ficoIssue = messages.some(function(e) { return /fico/i.test(e); });

    let list = '';
    if (messages.length === 1) {
      list = '<p class="text-sm text-red-700">' + escapeHtml(messages[0]) + '</p>';
    } else {
      list = '<ul class="text-sm text-red-700 text-left list-disc pl-5 space-y-1">' +
        messages.map(function(m) { return '<li>' + escapeHtml(m) + '</li>'; }).join('') + '</ul>';
    }

    panel.innerHTML = '<div class="sizer-result-animate text-center py-2">' +
      '<div class="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 text-red-600 mb-3">' +
        '<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>' +
      '</div>' +
      list +
      '<p class="text-xs text-gray-500 mt-4">' +
        (ficoIssue
          ? 'We lend from 600 FICO. If your score is close or improving, call ' + PHONE_HTML + ' and we will look at the deal.'
          : 'Guidelines change and close calls get a human review. Call ' + PHONE_HTML + ' or adjust the numbers above.') +
      '</p>' +
      '</div>';
    setPanelTitle('This deal does not meet guidelines as entered.');
    summaryBar.message('Outside guidelines as entered.', 'Adjust the numbers or call (833) FLIPMAX.');
    hideCta();
    setAriaStatus('This scenario does not meet guidelines: ' + messages.join('. ') + '.');
    track('sizer_result', { status: 'declined', reason: messages[0] });
    lastStatus = 'declined';
  }

  function renderPanelError(message) {
    pricedKey = null;
    setPanelTitle('The estimate could not be updated.');
    summaryBar.message('The estimate could not be updated.', message);
    panel.innerHTML = '<div class="text-center py-6">' +
      '<p class="text-sm text-gray-500">' + escapeHtml(message) + '</p>' +
      '</div>';
    hideCta();
    setAriaStatus(message);
    lastStatus = 'error';
  }

  function checkArvWarning() {
    const purchase = parseNumeric('purchase_price', getFieldValue('purchase_price')) || 0;
    const rehab = parseNumeric('renovation_budget', getFieldValue('renovation_budget')) || 0;
    const arv = parseNumeric('arv', getFieldValue('arv')) || 0;
    if (purchase > 0 && arv > 0 && arv < purchase + rehab) {
      showWarning('ARV is less than the purchase price plus renovation budget. This scenario may fall outside current guidelines, but you can still size it.');
    } else if (warning) {
      warning.classList.add('hidden');
      warning.textContent = '';
    }
  }

  // ── API calls ──
  function mapApiErrors(errors) {
    const perField = {};
    const general = [];
    if (Array.isArray(errors)) {
      errors.forEach(function(e) {
        const matched = Object.keys(fields).find(function(name) {
          const re = new RegExp(name.replace(/_/g, '[_\\s]'), 'i');
          return re.test(e);
        });
        if (matched) perField[matched] = e;
        else general.push(e);
      });
    } else if (errors && typeof errors === 'object') {
      Object.entries(errors).forEach(function(entry) {
        const key = entry[0];
        const val = entry[1];
        const msg = Array.isArray(val) ? val.join(' ') : String(val);
        if (fields[key]) perField[key] = fields[key].label + ' ' + msg;
        else general.push(msg);
      });
    }
    return { perField: perField, general: general };
  }

  function fetchEstimate() {
    if (isLoading) { pendingRefetch = true; return; }

    const errors = validate();
    if (remainingFields().length > 0 || Object.keys(errors).length > 0) {
      renderPanelIncomplete(errors);
      return;
    }

    if (isTeamSized()) {
      renderPanelTeamSized();
      return;
    }

    const payload = getRequestPayload();
    const key = JSON.stringify(payload);
    if (key === pricedKey && lastStatus === 'approved') { summaryBar.settle(); return; }

    const seq = ++requestSeq;
    isLoading = true;
    renderPanelLoading();
    if (submitBtn) submitBtn.disabled = true;

    const requests = [postSizer(payload)];
    const altLtps = payload.purchase_or_refi === 'purchase' ? sizer.CARD_LTPS : [];
    altLtps.forEach(function(ltp) {
      // Each structured answer is optional: without them the panel shows fewer cards.
      requests.push(postSizer(Object.assign({}, payload, { ltp: ltp })).catch(function() { return null; }));
    });

    Promise.all(requests)
    .then(function(results) {
      if (seq !== requestSeq) return;
      const resp = results[0].resp;
      const data = results[0].data || {};
      const alt = {};
      altLtps.forEach(function(ltp, i) {
        const r = results[i + 1];
        if (r && r.resp && r.resp.ok && r.data && r.data.status === 'approved') alt[ltp] = r.data;
      });

      if (resp.ok) {
        pricedKey = data.status === 'approved' ? key : null;
        if (data.status === 'approved') renderPanelApproved(data, alt, payload);
        else renderPanelDeclined(Array.isArray(data.errors) ? data.errors : []);
        return;
      }

      if (resp.status === 422) {
        const mapped = mapApiErrors(data.errors);
        Object.keys(mapped.perField).forEach(function(name) { showFieldError(name, mapped.perField[name]); });
        const firstField = Object.keys(mapped.perField)[0];
        renderPanelError(mapped.general[0] || (firstField ? mapped.perField[firstField] : 'This scenario does not meet current guidelines.'));
      } else if (resp.status === 429) {
        renderPanelError('You\'re updating too fast. Please wait a moment and try again.');
      } else if (resp.status === 403) {
        renderPanelError('This page is not authorized to use the sizer.');
      } else {
        renderPanelError('Unable to reach the sizer. Try again in a moment.');
      }
    })
    .catch(function() {
      if (seq !== requestSeq) return;
      renderPanelError('Unable to reach the sizer. Try again in a moment.');
    })
    .finally(function() {
      isLoading = false;
      if (submitBtn) {
        setTimeout(function() { submitBtn.disabled = false; }, COOLDOWN_MS);
      }
      if (pendingRefetch) {
        pendingRefetch = false;
        scheduleEstimate();
      }
    });
  }

  function postSizer(payload) {
    return fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function(resp) {
      return resp.json().then(function(data) {
        return { resp: resp, data: data };
      }).catch(function() {
        return { resp: resp, data: {} };
      });
    });
  }

  function scheduleEstimate() {
    const required = requiredFields();
    summaryBar.pending(required.length - remainingFields().length, required.length);
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(fetchEstimate, DEBOUNCE_MS);
  }

  // ── Storage ──
  function saveToStorage() {
    try {
      const data = {};
      Object.keys(fields).forEach(function(name) {
        data[name] = getFieldValue(name);
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {}
  }

  function loadFromStorage() {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      Object.keys(fields).forEach(function(name) {
        if (saved[name] !== undefined && saved[name] !== '') {
          setFieldValue(name, saved[name]);
        }
      });
    } catch (e) {}
  }

  function clearStorage() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(HANDOFF_KEY);
    } catch (e) {}
  }

  // Rentals show the purpose tabs; labels follow the product and purpose.
  function toggleProjectType() {
    const container = document.getElementById('purpose-container');
    const rental = getFieldValue('deal_type') === 'dscr';
    if (container) container.hidden = !rental;
    const mode = getFieldValue('purchase_or_refi') !== 'purchase' ? 'refi' : (getFieldValue('deal_type') === 'guc' ? 'guc' : 'rtl');
    form.querySelectorAll('[data-label-' + mode + ']').forEach(function(el) {
      el.textContent = el.getAttribute('data-label-' + mode);
    });
  }

  // ── Tabs ──
  function productFromFields() {
    const type = getFieldValue('deal_type');
    return type === 'guc' ? 'guc' : (type === 'dscr' ? 'dscr' : 'flip');
  }

  function syncProductTab() {
    const tab = form.querySelector('input[name="product"][value="' + productFromFields() + '"]');
    if (tab) tab.checked = true;
  }

  function applyProduct(key) {
    const product = PRODUCTS[key];
    if (!product) return;
    setFieldValue('deal_type', product.deal_type);
    if (product.purchase_or_refi) setFieldValue('purchase_or_refi', product.purchase_or_refi);
    else if (!getFieldValue('purchase_or_refi')) setFieldValue('purchase_or_refi', 'purchase');
    toggleProjectType();
  }

  // ── Event handlers ──
  form.addEventListener('input', function(e) {
    const name = e.target.name;
    if (name && fields[name]) {
      hideFieldError(name);
      if (name === 'state') e.target.value = e.target.value.replace(/[^a-z]/gi, '').toUpperCase().slice(0, 2);
      if (name === 'zip') e.target.value = e.target.value.replace(/\D/g, '').slice(0, 5);
      if (name === 'state') refreshCityOptions();
      if (isCurrencyField(name) && e.target.classList.contains('sizer-currency')) {
        formatCurrencyInput(e.target);
      }
      if (isCurrencyField(name)) checkArvWarning();
      saveToStorage();
      syncUrl();
      scheduleEstimate();
    }
  });

  form.addEventListener('change', function(e) {
    if (e.target.name === 'product') {
      applyProduct(e.target.value);
      saveToStorage();
      syncUrl();
      scheduleEstimate();
      return;
    }
    if (e.target.name && fields[e.target.name]) {
      if (e.target.name === 'purchase_or_refi' || e.target.name === 'deal_type') toggleProjectType();
      if (e.target.name === 'state') {
        refreshCityOptions();
        const msg = getFieldValue('city') ? validateField('city') : null;
        if (msg) showFieldError('city', msg); else hideFieldError('city');
      }
      saveToStorage();
      syncUrl();
      scheduleEstimate();
    }
  });

  form.addEventListener('submit', function(e) {
    e.preventDefault();
    clearTimeout(debounceTimer);
    clearAllErrors();
    checkArvWarning();

    const errors = validate();
    if (Object.keys(errors).length > 0) {
      Object.entries(errors).forEach(function(entry) {
        showFieldError(entry[0], entry[1]);
      });
      showSummary('Please fix the highlighted fields and try again.');
      renderPanelIncomplete(errors);
      const firstInvalid = form.querySelector('[aria-invalid="true"]');
      if (firstInvalid && firstInvalid.focus) firstInvalid.focus();
      return;
    }

    if (!isLoading) fetchEstimate();
    if (panelWrap && panelWrap.scrollIntoView && window.innerWidth < 1024) {
      panelWrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  if (resetBtn) {
    resetBtn.addEventListener('click', function() {
      clearTimeout(debounceTimer);
      requestSeq++;
      form.reset();
      clearAllErrors();
      clearStorage();
      form.querySelectorAll('.sizer-currency').forEach(function(input) { input.value = ''; });
      toggleProjectType();
      syncProductTab();
      lastStatus = null;
      currentOffer = null;
      linkedLtp = null;
      renderPanelIncomplete();
      syncUrl();
    });
  }

  // ── Capture ──
  // Name, mobile, email under the estimate. Posts them with the sizer inputs
  // and the selected offer. On success the form is replaced in place by the
  // next step: the loan officer's first name and the two proposed call times.
  // Field feedback is in words, at the moment of the error.
  const capture = (function() {
    const formEl = document.getElementById('quote-capture');
    const done = document.getElementById('quote-capture-done');
    const lead = document.getElementById('quote-capture-lead');
    const button = document.getElementById('quote-capture-button');
    const errorBox = document.getElementById('quote-capture-error');
    if (!formEl || !done) return { show: function() {}, hide: function() {} };

    const BUTTON_TEXT = 'Text me two call times';
    const inputs = {
      name: document.getElementById('quote-capture-name'),
      phone: document.getElementById('quote-capture-phone'),
      email: document.getElementById('quote-capture-email')
    };
    const validators = {
      name: function(v) { return v.trim() ? null : 'Enter your name.'; },
      phone: function(v) { return v.replace(/\D/g, '').length >= 10 ? null : 'Enter a 10-digit mobile number.'; },
      email: function(v) { return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim()) ? null : 'Enter a valid email address.'; }
    };
    let sending = false;
    let submitted = false;

    function fieldError(name, msg) {
      const el = formEl.querySelector('[data-error-for="' + name + '"]');
      const input = inputs[name];
      if (el) { el.textContent = msg || ''; el.classList.toggle('hidden', !msg); }
      if (input) {
        input.classList.toggle('border-red-500', !!msg);
        if (msg) input.setAttribute('aria-invalid', 'true'); else input.removeAttribute('aria-invalid');
      }
    }

    function showError(msg) {
      if (!errorBox) return;
      errorBox.textContent = msg || '';
      errorBox.classList.toggle('hidden', !msg);
    }

    function validateAll() {
      const errors = {};
      Object.keys(validators).forEach(function(n) {
        const msg = validators[n](inputs[n].value);
        if (msg) errors[n] = msg;
      });
      return errors;
    }

    function prefill() {
      try {
        const saved = JSON.parse(localStorage.getItem(CONTACT_KEY) || '{}');
        const fullName = [saved.firstName, saved.lastName].filter(Boolean).join(' ');
        if (!inputs.name.value && fullName) inputs.name.value = fullName;
        if (!inputs.phone.value && saved.phone) inputs.phone.value = saved.phone;
        if (!inputs.email.value && saved.email) inputs.email.value = saved.email;
      } catch (e) {}
    }

    function rememberContact(name, phone, email) {
      try {
        const parts = name.split(/\s+/);
        localStorage.setItem(CONTACT_KEY, JSON.stringify({
          firstName: parts.shift() || '',
          lastName: parts.join(' '),
          phone: phone,
          email: email
        }));
      } catch (e) {}
    }

    function setSending(on) {
      sending = on;
      if (button) {
        button.disabled = on;
        button.textContent = on ? 'Sending...' : BUTTON_TEXT;
      }
    }

    function setText(id, text) {
      const el = document.getElementById(id);
      if (el) el.textContent = text || '';
    }

    function showDone(json, phone) {
      const lo = (json.loanOfficer && json.loanOfficer.firstName) || 'A loan officer';
      const times = Array.isArray(json.proposedTimes) ? json.proposedTimes : [];
      setText('quote-capture-lo', lo);
      setText('quote-capture-lo-again', lo);
      setText('quote-capture-phone-echo', phone);
      setText('quote-capture-time-1', times[0] ? times[0].label : 'a time today');
      setText('quote-capture-time-2', times[1] ? times[1].label : 'a time tomorrow');
      setText('quote-capture-ref', json.referenceNumber || 'pending');
      formEl.classList.add('hidden');
      done.classList.remove('hidden');
      submitted = true;
      setAriaStatus(lo + ' will text ' + phone + ' with two call times. Reference ' + (json.referenceNumber || 'pending') + '.');
    }

    function show(leadText) {
      summaryBar.setAction('capture');
      if (submitted) { done.classList.remove('hidden'); return; }
      if (lead && leadText) lead.textContent = leadText;
      prefill();
      formEl.classList.remove('hidden');
      done.classList.add('hidden');
    }

    function hide() {
      summaryBar.setAction('estimate');
      formEl.classList.add('hidden');
      done.classList.add('hidden');
    }

    Object.keys(inputs).forEach(function(n) {
      inputs[n].addEventListener('blur', function() { fieldError(n, validators[n](inputs[n].value)); });
      inputs[n].addEventListener('input', function() { if (!validators[n](inputs[n].value)) fieldError(n, null); });
    });

    formEl.addEventListener('submit', function(e) {
      e.preventDefault();
      if (sending) return;
      showError(null);
      const errors = validateAll();
      Object.keys(inputs).forEach(function(n) { fieldError(n, errors[n]); });
      if (Object.keys(errors).length > 0) {
        inputs[Object.keys(errors)[0]].focus();
        return;
      }

      const name = inputs.name.value.trim();
      const phone = inputs.phone.value.trim();
      const email = inputs.email.value.trim();
      setSending(true);
      track('sizer_submit_open', { status: lastStatus || 'unknown' });

      fetch(QUICK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          name: name,
          phone: phone,
          email: email,
          inputs: getScenario(),
          offer: currentOffer
        })
      }).then(function(resp) {
        return resp.json().catch(function() { return {}; }).then(function(json) { return { ok: resp.ok, json: json }; });
      }).then(function(result) {
        setSending(false);
        if (!result.ok || !result.json.success) {
          const msgs = Array.isArray(result.json.errors) ? result.json.errors : [];
          showError(msgs.length ? msgs.join(' ') : 'We could not send this. Try again or call (833) FLIPMAX.');
          return;
        }
        rememberContact(name, phone, email);
        track('sizer_submit', { status: lastStatus || 'unknown', reference: result.json.referenceNumber });
        try {
          if (typeof window.fbq === 'function') window.fbq('track', 'Lead', { content_category: 'quote' }, { eventID: result.json.referenceNumber });
        } catch (e) {}
        showDone(result.json, phone);
      }).catch(function() {
        setSending(false);
        showError('No connection. Try again or call (833) FLIPMAX.');
      });
    });

    return { show: show, hide: hide };
  })();

  // Blur validation for individual fields
  form.querySelectorAll('input, select').forEach(function(input) {
    input.addEventListener('blur', function() {
      const name = input.name;
      if (!name || !fields[name]) return;
      const msg = validateField(name);
      if (msg) showFieldError(name, msg);
      else hideFieldError(name);
    });
  });

  // ── Address autocomplete ──
  // Google Places loads on the first focus of the address field, only when
  // the page carries a key. A picked address fills city, state, and ZIP and
  // fires input on each so the estimate and the link follow.
  (function() {
    const input = form.elements['address'];
    const key = input && input.getAttribute('data-places-key');
    if (!key) return;
    let requested = false;

    function fill(name, value) {
      const el = form.elements[name];
      if (!el || value === undefined || value === null) return;
      el.value = value;
      el.dispatchEvent(new Event('input', { bubbles: true }));
    }

    function attach() {
      const auto = new google.maps.places.Autocomplete(input, {
        componentRestrictions: { country: 'us' },
        fields: ['address_components'],
        types: ['address']
      });
      auto.addListener('place_changed', function() {
        const parts = {};
        ((auto.getPlace() || {}).address_components || []).forEach(function(c) {
          c.types.forEach(function(t) { parts[t] = c; });
        });
        const long = function(t) { return parts[t] ? parts[t].long_name : ''; };
        const street = [long('street_number'), long('route')].filter(Boolean).join(' ');
        if (street) fill('address', street);
        const city = long('locality') || long('sublocality_level_1') || long('neighborhood') || long('postal_town');
        if (city) fill('city', city);
        if (parts.administrative_area_level_1) fill('state', parts.administrative_area_level_1.short_name);
        if (parts.postal_code) fill('zip', parts.postal_code.long_name);
        track('sizer_address_autocomplete');
      });
    }

    input.addEventListener('focus', function() {
      if (requested) return;
      requested = true;
      if (window.google && window.google.maps && window.google.maps.places) { attach(); return; }
      window.initQuotePlaces = attach;
      const script = document.createElement('script');
      script.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key) + '&libraries=places&callback=initQuotePlaces';
      script.async = true;
      document.head.appendChild(script);
    });
  })();

  // Initialize. A shared link wins over whatever this browser last typed;
  // otherwise the last scenario comes back from storage.
  if (quoteLink.isShared(location.search)) loadFromUrl();
  else loadFromStorage();
  // A saved flip or build is always a purchase.
  applyProduct(productFromFields());
  syncProductTab();
  form.querySelectorAll('.sizer-currency').forEach(formatCurrencyInput);
  checkArvWarning();
  if (remainingFields().length === 0 && Object.keys(validate()).length === 0) {
    scheduleEstimate();
  } else {
    renderPanelIncomplete();
  }
})();
